import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterCategories } from "../utils/searchFilterCategories";
import { fetchCategories } from "@/services/catalogueApi";
import { useBusiness } from "@hooks/useBusiness";

// Firestore stores categoryId/categoryName; the table reads id/title.
function toRows(categories) {
  return categories.map((c) => ({
    ...c,
    id: c.categoryId,
    title: c.categoryName,
  }));
}

export default function useCategoriesTable(searchTerm = "") {
  const { businessId } = useBusiness();

  const {
    data: categories = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["inventory-categories", businessId],
    queryFn: async () => toRows(await fetchCategories(businessId)),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredCategories = useMemo(
    () => searchFilterCategories(categories, searchTerm),
    [categories, searchTerm]
  );

  const categoryColumns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Category",
        cell: ({ row }) => (
          <span className="font-medium text-[#1a1a1a]">{row.original.title}</span>
        ),
      },
      {
        accessorKey: "productCount",
        header: "Products",
        cell: ({ row }) => row.original.productCount ?? 0,
      },
      {
        accessorKey: "quantity",
        header: "Total Units",
        cell: ({ row }) => row.original.quantity ?? 0,
      },
      {
        accessorKey: "multiple",
        header: "Type",
        cell: ({ row }) => (
          <span className="text-[13px] text-[#555]">
            {row.original.multiple ? "Multi-select" : "Single"}
          </span>
        ),
      },
    ],
    []
  );

  const renderCategoryRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allCategories: categories,
    categories: filteredCategories,
    categoryColumns,
    renderCategoryRow,
    refetch,
    isLoading,
    isError,
  };
}
